import { SystemStyleObject } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'

export type { ColorScheme }

// ─── Font family constants (matches packages/dark/src/theme/semantic/textStyles.ts) ─
const soehne = "'Soehne', sans-serif"
const soehneMono = "'Soehne Mono', sans-serif"
// Emphasis/bold text uses Soehne Kraftig at weight 500, never italic Soehne
// at its default weight — see packages/dark/CLAUDE.md's Fonts section.
const soehneKraftig = "'Soehne Kraftig', sans-serif"

/**
 * Semantic token names resolved by DarkProvider (Chakra v3).
 * Light tokens use the `light.*` namespace defined in
 * packages/dark/src/theme/semantic/lightColors.ts.
 */
const tokens = {
  dark: {
    bgInput: 'bg.input',
    bgCard: 'bg.card',
    bgCardElevated: 'bg.card-elevated',
    textPrimary: 'text.primary',
    textSecondary: 'text.secondary',
    textTertiary: 'text.tertiary',
    placeholder: 'text.search-placeholder',
    bgSectionHeader: 'transparent',
    borderSearch: 'border.search',
    borderHover: 'border.input-hover',
    focusRing: 'interactive.focus-ring',
    sectionDivider: 'border.section-divider',
    menuDivider: 'border.menu-divider',
    // Checked selection controls all carry the same brand accent as the
    // Radio's on state (interactive/radio-on).
    bgCheckboxOn: 'interactive.radio-on',
    bgTabActive: 'bg.tab-active',
    // Unchecked boxes are hollow, matching TableCheckbox — a filled
    // bg.tab-active square reads as half-selected on a dark dropdown.
    bgCheckboxOff: 'transparent',
    // Same unchecked border as TableCheckbox (interactive.tertiary-border)
    // so dropdown checkboxes match the incidents table.
    checkboxBorder: 'interactive.tertiary-border',
  },
  light: {
    bgInput: 'light.bg.input',
    bgCard: 'light.bg.card',
    bgCardElevated: 'light.bg.card-elevated',
    textPrimary: 'light.text.primary',
    textSecondary: 'light.text.secondary',
    textTertiary: 'light.text.tertiary',
    placeholder: 'light.text.search-placeholder',
    bgSectionHeader: 'light.bg.section-header',
    borderSearch: 'light.border.search',
    borderHover: 'light.border.input-hover',
    focusRing: 'light.interactive.focus-ring',
    sectionDivider: 'light.border.section-divider',
    menuDivider: 'light.border.menu-divider',
    bgCheckboxOn: 'light.interactive.radio-on',
    bgTabActive: 'light.bg.tab-active',
    bgCheckboxOff: 'white',
    checkboxBorder: 'light.interactive.tertiary-border',
  },
}

export const searchInputStyles = (
  isActive: boolean,
  colorScheme: ColorScheme = 'dark',
  height: string | number = '36px',
  showIcon: boolean = true
): SystemStyleObject => ({
  display: 'block',
  width: '100%',
  height: typeof height === 'number' ? `${height}px` : height,
  pl: showIcon ? '34px' : '8px',
  pr: '8px',
  borderRadius: '0px',
  fontFamily: soehne,
  fontSize: '13px',
  bg: tokens[colorScheme].bgInput,
  color: tokens[colorScheme].textPrimary,
  border: '1px solid',
  borderColor: isActive
    ? tokens[colorScheme].focusRing
    : tokens[colorScheme].borderSearch,
  outline: 'none',
  _placeholder: { color: tokens[colorScheme].placeholder },
  _hover: {
    borderColor: isActive
      ? tokens[colorScheme].focusRing
      : tokens[colorScheme].borderHover,
  },
  transition: 'border-color 0.15s ease',
})

export const dropdownStyles = (
  colorScheme: ColorScheme = 'dark',
  isOpenAbove = false
): SystemStyleObject => ({
  fontFamily: soehne,
  bg: tokens[colorScheme].bgCard,
  border: '1px solid',
  borderColor: tokens[colorScheme].borderSearch,
  ...(isOpenAbove ? { borderBottom: 'none' } : { borderTop: 'none' }),
  borderRadius: isOpenAbove ? '6px 6px 0 0' : '0 0 6px 6px',
  overflowX: 'hidden',
  boxShadow: 'lg',
})

export const dropdownItemStyles = (
  isActive: boolean,
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  px: '12px',
  py: '8px',
  fontFamily: soehne,
  fontSize: '13px',
  cursor: 'pointer',
  color: tokens[colorScheme].textPrimary,
  bg: isActive ? tokens[colorScheme].bgCardElevated : 'transparent',
  _hover: { bg: tokens[colorScheme].bgCardElevated },
  userSelect: 'none',
})

export const primaryActionContainerStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  borderTop: '1px solid',
  borderColor: tokens[colorScheme].menuDivider,
})

export const primaryActionHeaderStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  px: '10px',
  pt: '4px',
  pb: '4px',
  // accent text style: Söhne Mono Buch · 12/20 · 1px (see textStyles.ts)
  fontFamily: soehneMono,
  fontSize: '12px',
  fontWeight: '400',
  lineHeight: '20px',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  textAlign: 'left',
  color:
    colorScheme === 'light'
      ? tokens.light.textPrimary
      : tokens.dark.placeholder,
  bg:
    colorScheme === 'light' ? tokens.light.bgCard : tokens.dark.bgSectionHeader,
  userSelect: 'none',
})

export const primaryActionItemStyles = (
  isActive: boolean,
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  px: '12px',
  py: '8px',
  fontFamily: soehne,
  fontSize: '13px',
  cursor: 'pointer',
  color: tokens[colorScheme].textPrimary,
  bg: isActive ? tokens[colorScheme].bgCardElevated : 'transparent',
  _hover: { bg: tokens[colorScheme].bgCardElevated },
  userSelect: 'none',
})

export const noResultsItemStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  px: '12px',
  py: '8px',
  fontFamily: soehne,
  fontSize: '13px',
  color: tokens[colorScheme].textSecondary,
  userSelect: 'none',
})

// ─── Checkbox option styles (for multi-select mode) ──────────────────────────

// Data-URI glyphs, so the stroke can't come from a token reference. Strokes
// contrast with the checked accent fill per scheme: text/on-accent
// (darkBlue.900) on signal yellow, white on the legacy teal.
const checkmarkSvg = (stroke: string) =>
  `url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 8.5L6.5 12L13 4' stroke='${encodeURIComponent(
    stroke
  )}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`

const dashSvg = (stroke: string) =>
  `url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 8H12' stroke='${encodeURIComponent(
    stroke
  )}' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E")`

const CHECK_STROKE: Record<ColorScheme, string> = {
  dark: '#000A0F',
  light: '#FFFFFF',
}

export const checkboxOptionRowStyles = (
  isActive: boolean,
  colorScheme: ColorScheme = 'dark',
  isReadOnly = false
): SystemStyleObject => ({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  px: '10px',
  // One layout for both schemes — only colors differ between light and dark.
  // flexShrink 0 is load-bearing: rows have overflow hidden (min-height 0 as
  // flex children), so without it long lists compress the rows into
  // overlapping lines instead of scrolling.
  flexShrink: 0,
  minH: '40px',
  cursor: isReadOnly ? 'not-allowed' : 'pointer',
  opacity: isReadOnly ? 0.5 : 1,
  color: tokens[colorScheme].textPrimary,
  bg: isActive ? tokens[colorScheme].bgCardElevated : 'transparent',
  _hover: isReadOnly ? undefined : { bg: tokens[colorScheme].bgCardElevated },
  userSelect: 'none',
  fontFamily: soehne,
  fontSize: '14px',
  fontWeight: '400',
  overflow: 'hidden',
})

export const checkboxOptionSubtitleStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  color: tokens[colorScheme].textSecondary,
  display: 'block',
  fontSize: '12px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

export const checkboxOptionCaptionStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  color: tokens[colorScheme].textTertiary,
  display: 'block',
  fontFamily: soehneKraftig,
  fontSize: '11px',
  fontStyle: 'italic',
  fontWeight: '500',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

export const checkboxStyles = (
  isSelected: boolean,
  colorScheme: ColorScheme = 'dark',
  isIndeterminate?: boolean
): SystemStyleObject => {
  const isFilled = isSelected
  const stroke = CHECK_STROKE[colorScheme]
  const bgImage = isSelected
    ? isIndeterminate
      ? dashSvg(stroke)
      : checkmarkSvg(stroke)
    : 'none'
  return {
    appearance: 'none',
    display: 'inline-block',
    width: '16px',
    height: '16px',
    minWidth: '16px',
    borderWidth: '1.5px',
    borderStyle: 'solid',
    borderColor: isFilled
      ? tokens[colorScheme].bgCheckboxOn
      : tokens[colorScheme].checkboxBorder,
    // Sharp corners — no exceptions (Checkbox spec).
    borderRadius: '0',
    cursor: 'pointer',
    flexShrink: 0,
    bg: isFilled
      ? tokens[colorScheme].bgCheckboxOn
      : tokens[colorScheme].bgCheckboxOff,
    backgroundImage: bgImage,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: '12px 12px',
    pointerEvents: 'none',
  }
}

/**
 * Removes all visual chrome (border, box-shadow, border-radius) from the
 * dropdown so it renders flush inside a parent container such as
 * BulkTypeaheadSearch. All three properties are coupled intentionally —
 * together they represent a single "flat" design concept where the dropdown
 * blends seamlessly into its enclosing panel.
 */
export const flatDropdownStyles = (): SystemStyleObject => ({
  border: 'none',
  boxShadow: 'none',
  borderRadius: '0',
})

export const footerRowStyles = (
  colorScheme: ColorScheme = 'dark'
): SystemStyleObject => ({
  borderTop: '1px solid',
  borderColor: tokens[colorScheme].menuDivider,
  px: '8px',
  py: '8px',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '8px',
})
